import React from "react";
import { connect } from "react-redux";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
// Paperview PDF viewer component
import { PDFViewer as PaperviewPDF } from "@paperviewpdf/react-viewer";
import { loadDocumentUrl } from "../../lib/axios-client";
import security from "../../services/Security";
import httpClient from "../../lib/HttpClient";
import { waitForPdfReady } from "../../lib/pdfReadiness";
import history from "../../history";
import notificationService from "../../services/Notifications";
import referencesService from "../../services/References";
import { SAVING_CONTENT_STATES } from "./Viewer";
import fetchDocumentClaims from "../../actions/document/fetchDocumentClaims";
import fetchDocumentReferences from "../../actions/document/fetchDocumentReferences";
import fetchDocumentConversations from "../../actions/document/fetchDocumentConversations";
import Spinner from "../Common/Spinner";
import styles from "./PaperViewer.module.scss";
import { isShapeAnnotationType } from "../../pdf/shapeAnnotations";
import {
  PIN_TYPES,
  getActivePinType,
  getPinTools,
  importPinXfdf,
  isPinAnnotation,
  registerPinCommands,
  togglePinTool,
  updatePinToolbar,
} from "../../pdf/pinDrop";
import { findingsToRegions } from "../../pdf/reviewRegions";

import "../../lib/paperviewIcons";
// The user's last zoom level, shared across documents and sessions.
// Parity with the PDFTron viewer, which persisted "zoomLevel" the same way.
const ZOOM_LEVEL_STORAGE_KEY = "paperviewZoomLevel";
const ZOOM_MODES = ["automatic", "fit-page", "fit-width"];

// The main viewer offers both pin drops: a comment pin and a reference pin.
const PIN_DROP_TYPES = [PIN_TYPES.COMMENT, PIN_TYPES.REFERENCE];
const PIN_DROP_TOOLS = getPinTools(PIN_DROP_TYPES);

// What a drawn shape can become, chosen from a popup after it is drawn.
const SHAPE_INTENTS = {
  COMMENT: "comment",
  REFERENCE: "reference",
};

// The Comment / Reference popup of a freshly drawn shape is Paperview PDF's
// annotation selection menu. It must only show while a shape is waiting for
// that choice, and the only runtime switch Paperview PDF has for hiding a
// menu is disabling one of its categories. Category hide rules are generated
// from the viewer's built-in UI schema, so a category of our own would have
// no rule: this borrows a built-in one from the redaction tools, which
// Papercurve does not use (the "mode-redact" category is disabled).
const SHAPE_INTENT_MENU_CATEGORY = "redaction-clear";

// A shape is selected right after it is drawn so its popup shows. The end of
// the drawing gesture can deselect it again; a deselect this soon after the
// shape was drawn is not the user abandoning it.
const SHAPE_INTENT_SETTLE_MS = 300;

/**
 * Reads the stored zoom level: a zoom mode string or a numeric scale
 * factor. Returns null when nothing valid is stored.
 *
 * @returns {string|number|null}
 */
const loadStoredZoomLevel = function () {
  var stored = localStorage.getItem(ZOOM_LEVEL_STORAGE_KEY);
  if (!stored) return null;

  if (ZOOM_MODES.indexOf(stored) !== -1) return stored;

  var numeric = parseFloat(stored);
  var isSaneScale = !isNaN(numeric) && numeric >= 0.1 && numeric <= 10;
  if (isSaneScale) return numeric;

  return null;
};

// Register custom Paperview PDF icons (reference, claim)

/**
 * Generate a human-readable annotation ID encoding who created it and where.
 * PaperView PDF does NOT auto-generate IDs — callers must provide one.
 * Format: d{docId}_u{userId}_p{page}_{random}
 * Example: d42_u15_p3_a4f2k
 *
 * @param {number|string} docId - Document ID
 * @param {number|string} userId - User ID
 * @param {number} pageIndex - Zero-based page index
 * @returns {string} Human-readable annotation ID
 */
function generateAnnotationId(docId, userId, pageIndex) {
  var rand = Math.random().toString(36).substr(2, 5);
  return "d" + docId + "_u" + userId + "_p" + pageIndex + "_" + rand;
}

class PaperViewer extends React.Component {
  constructor(props) {
    super(props);

    this.viewer = React.createRef();

    // Paperview PDF references
    this.registry = null;
    this.annotationPlugin = null;
    this.selectionPlugin = null;
    this.xfdfPlugin = null;
    this.documentManagerPlugin = null;
    this.zoomPlugin = null;
    this.commandsPlugin = null;
    // Pin type of the pin tool the user last switched on (see togglePinTool)
    this.pendingPinType = null;
    // Ids of shapes and pins drawn in this session. Until their comment or
    // reference is saved, moving them must update the XFDF that is waiting
    // to be saved (see refreshUnsavedAnnotationXfdf).
    this.drawnAnnotationIds = {};
    // Shape just drawn that is waiting for the user to choose Comment or
    // Reference from its popup: { id, pageIndex, drawnAt }.
    this.pendingShape = null;
    // Id of an annotation this component deselects or deletes itself; the
    // resulting deselect must not be reported to the parent as a user action.
    this.silentDeselectId = null;
    this.uiPlugin = null;
    this.scrollPlugin = null;

    // Guard: annotation IDs being hidden via toggle — event handlers skip these
    this.toggleHiddenAnnotationIds = {};

    this.state = {
      webViewerLoaded: false,
      viewerReady: false, // Paperview PDF ready state
      pdfReady: false, // Backend PDF rendition ready — gates viewer init
      pdfTimedOut: false, // Rendition still pending after the poll timeout
      selectedQuads: null,
      currentPageIndex: null,
      pageIndex: null,
      currentAnnotation: null,
      currentAnnotationXfdf: null,
      currentAnnotationText: null,
      initialDocumentLoadTriggered: false,
      referenceAnnotationIds: [],
      conversationAnnotationIds: [],
      claimAnnotationIds: [],
      nativeAnnotationSnapshot: [],
      conversations: [],
      references: [],
      hasFormFields: false,
      claimXFDF: "",
      deletedDocumentClaim: null,
      outsideAnnotations: [],
    };
  }

  shouldComponentUpdate = (nextProps, nextState) => {
    // The viewer manages its own updates through the plugin APIs; React
    // re-renders only when the PDF-readiness gate changes (see render()).
    return (
      nextState.pdfReady !== this.state.pdfReady ||
      nextState.pdfTimedOut !== this.state.pdfTimedOut
    );
  };

  componentWillReceiveProps = async (nextProps) => {
    // ===========================================
    // Annotation Layer Visibility Toggles
    // ===========================================
    // These respond to toggle changes from the Annotations dropdown in Viewer.js.
    // Hide = delete annotations from PaperView PDF (they're removed from render).
    // Show = re-import annotations from backend XFDF data.

    // "All Papercurve" toggle — hides/shows comments, references, and claims together
    if (nextProps.hideAllAnnotations !== this.props.hideAllAnnotations) {
      if (nextProps.hideAllAnnotations) {
        this.hideAllCommentsPaperView();
        this.hideAllReferencesPaperView();
        this.hideAllClaimsPaperView();
      } else {
        this.showAllCommentsPaperView();
        this.showAllReferencesPaperView();
        await this.showAllClaimsPaperView();
      }
    }

    // "Imported Annotations" toggle — hides/shows native PDF annotations
    if (nextProps.hideNativeAnnotations !== this.props.hideNativeAnnotations) {
      if (nextProps.hideNativeAnnotations) {
        this.hideNativeAnnotationsPaperView();
      } else {
        this.showNativeAnnotationsPaperView();
      }
    }

    // "Comments" toggle
    if (nextProps.hideComments !== this.props.hideComments) {
      if (nextProps.hideComments) {
        this.hideAllCommentsPaperView();
      } else {
        this.showAllCommentsPaperView();
      }
    }

    // "References" toggle
    if (nextProps.hideReferences !== this.props.hideReferences) {
      if (nextProps.hideReferences) {
        this.hideAllReferencesPaperView();
      } else {
        this.showAllReferencesPaperView();
      }
    }

    // "Product Claims" toggle
    if (nextProps.hideClaims !== this.props.hideClaims) {
      if (nextProps.hideClaims) {
        this.hideAllClaimsPaperView();
      } else {
        await this.showAllClaimsPaperView();
      }
    }

    // ===========================================
    // Other Prop Changes
    // ===========================================

    // Document changed — reload claims and document
    if (nextProps.docId !== this.props.docId) {
      // Stop waiting on the previous document's PDF rendition
      if (this.cancelPdfReadyWait) {
        this.cancelPdfReadyWait();
        this.cancelPdfReadyWait = null;
      }
      await this.props.fetchDocumentClaims(nextProps.docId);
      this.props.loadDocument(nextProps.docId, true);
    }

    // Conversation status toggled — reload conversations
    if (nextProps.convoStatusToggled && nextProps.updateConvos) {
      this.reloadConversationsPaperView(this.props.documentConversations);
    }

    // Update selection menu when mode-related props change
    if (
      nextProps.readOnly !== this.props.readOnly ||
      nextProps.inRedefineMode !== this.props.inRedefineMode ||
      nextProps.versionId !== this.props.versionId
    ) {
      this.updateSelectionMenu(nextProps);
    }

    // Show or hide the pin button when editing becomes allowed or not
    if (
      nextProps.readOnly !== this.props.readOnly ||
      nextProps.isApproved !== this.props.isApproved ||
      nextProps.inRedefineMode !== this.props.inRedefineMode ||
      nextProps.versionId !== this.props.versionId ||
      nextProps.docId !== this.props.docId
    ) {
      this.updatePinToolbar(nextProps);
    }
  };

  componentDidMount = () => {
    window.addEventListener("resize", this.onResize);
    this.onResize();

    // Expose PaperViewer instance to parent (Viewer.js) via prop callback.
    // The Redux connect() HOC wraps this component, so a normal ref gives
    // the HOC wrapper — not this class instance. This bypasses that.
    if (this.props.onRef) {
      this.props.onRef(this);
    }

    this.startPdfReadyWait();
  };

  componentWillUnmount() {
    window.removeEventListener("resize", this.onResize);

    if (this.zoomUnsubscribe) {
      this.zoomUnsubscribe();
      this.zoomUnsubscribe = null;
    }

    if (this.cancelPdfReadyWait) {
      this.cancelPdfReadyWait();
      this.cancelPdfReadyWait = null;
    }

    // Clear parent reference
    if (this.props.onRef) {
      this.props.onRef(null);
    }

    // Paperview PDF cleanup - the component handles its own cleanup
    // Clear plugin references
    this.registry = null;
    this.comparePlugin = null;
    this.reviewRegionsDocumentId = null;
    this.annotationPlugin = null;
    this.selectionPlugin = null;
    this.xfdfPlugin = null;
    this.documentManagerPlugin = null;
    this.zoomPlugin = null;
    this.scrollPlugin = null;
  }

  onResize = () => {
    const header = document.getElementById("usersScreenHeader");
    const webViewerContainer = document.getElementById("webViewerContainer");

    // caculate offset to subtract
    let offset = header.offsetHeight + 70;

    const newHeight = window.innerHeight - offset;

    webViewerContainer.setAttribute("style", `height:${newHeight}px`);
  };

  // ===========================================
  // Paperview PDF Methods
  // ===========================================

  /**
   * Registers custom commands for the text selection popup.
   * Each command creates a specific type of annotation from selected text.
   */
  registerSelectionCommands = () => {
    if (!this.commandsPlugin) return;

    var _that = this;

    this.commandsPlugin.registerCommand({
      id: "papercurve:comment",
      label: "Comment",
      icon: "comment",
      action: function () {
        _that.pickCommentPaperView();
      },
    });

    this.commandsPlugin.registerCommand({
      id: "papercurve:reference",
      label: "Reference",
      icon: "papercurveReference",
      action: function () {
        _that.pickReferencePaperView();
      },
    });

    this.commandsPlugin.registerCommand({
      id: "papercurve:strikeout",
      label: "Strikeout",
      icon: "strikethrough",
      action: function () {
        _that.pickStrikeoutPaperView();
      },
    });

    this.commandsPlugin.registerCommand({
      id: "papercurve:claim",
      label: "Claim",
      icon: "papercurveClaim",
      action: function () {
        _that.pickClaimPaperView();
      },
    });
  };

  /**
   * Registers the commands of the popup shown on a freshly drawn shape:
   * Comment and Reference (see offerShapeIntent).
   */
  registerShapeIntentCommands = () => {
    if (!this.commandsPlugin) return;

    var _that = this;

    this.commandsPlugin.registerCommand({
      id: "papercurve:shape-comment",
      label: "Comment",
      icon: "comment",
      action: function () {
        _that.chooseShapeIntent(SHAPE_INTENTS.COMMENT);
      },
    });

    this.commandsPlugin.registerCommand({
      id: "papercurve:shape-reference",
      label: "Reference",
      icon: "papercurveReference",
      action: function () {
        _that.chooseShapeIntent(SHAPE_INTENTS.REFERENCE);
      },
    });
  };

  /**
   * Updates the selection menu schema based on the current document mode.
   * Mirrors PDFTron's textPopup.update() conditional logic.
   */
  updateSelectionMenu = (props) => {
    if (!this.uiPlugin) return;

    var items = [];
    var isPastVersion = props.versionId && props.versionId !== props.docId;

    if (isPastVersion) {
      // Past version: no selection menu items
      items = [];
    } else if (props.readOnly) {
      // Read-only (approved doc): only claims
      items = [
        {
          type: "command-button",
          id: "pc-claim",
          commandId: "papercurve:claim",
          variant: "icon",
        },
      ];
    } else if (props.inRedefineMode) {
      // Redefine mode: only reference
      items = [
        {
          type: "command-button",
          id: "pc-reference",
          commandId: "papercurve:reference",
          variant: "icon",
        },
      ];
    } else {
      // Normal editing mode: all 4 actions
      items = [
        {
          type: "command-button",
          id: "pc-comment",
          commandId: "papercurve:comment",
          variant: "icon",
        },
        {
          type: "command-button",
          id: "pc-reference",
          commandId: "papercurve:reference",
          variant: "icon",
        },
        {
          type: "command-button",
          id: "pc-strikeout",
          commandId: "papercurve:strikeout",
          variant: "icon",
        },
        {
          type: "command-button",
          id: "pc-claim",
          commandId: "papercurve:claim",
          variant: "icon",
        },
      ];
    }

    this.uiPlugin.mergeSchema({
      selectionMenus: {
        selection: {
          id: "selection",
          items: items,
        },
        // Popup of a freshly drawn shape. Hidden through its category
        // except while a shape is waiting for the choice (offerShapeIntent).
        annotation: {
          id: "pc-shape-intent-menu",
          categories: [SHAPE_INTENT_MENU_CATEGORY],
          items: [
            {
              type: "command-button",
              id: "pc-shape-comment",
              commandId: "papercurve:shape-comment",
              variant: "icon",
            },
            {
              type: "command-button",
              id: "pc-shape-reference",
              commandId: "papercurve:shape-reference",
              variant: "icon",
            },
          ],
        },
      },
    });
  };

  /**
   * Tells whether pins may be dropped on the document in its current mode.
   * Editing stops completely on approved documents and past versions, and
   * redefine mode only allows re-highlighting an existing reference.
   *
   * @param {Object} props - component props to evaluate
   * @returns {boolean}
   */
  canDropPins = (props) => {
    var isPastVersion = props.versionId && props.versionId !== props.docId;

    if (isPastVersion) return false;
    if (props.readOnly) return false;
    if (props.isApproved) return false;
    if (props.inRedefineMode) return false;

    return true;
  };

  /**
   * Returns the pin type of the annotation tool that is currently active.
   *
   * @returns {string|null} a PIN_TYPES value, or null when no pin tool is active
   */
  getActivePinType = () => {
    return getActivePinType(this.annotationPlugin);
  };

  /**
   * Switches a pin tool on, or off when it is already the active tool.
   * The next click on a page then drops a pin of that type; the tool
   * switches itself off after one pin.
   *
   * @param {string} pinType - a PIN_TYPES value
   */
  togglePinTool = (pinType) => {
    if (!this.canDropPins(this.props)) return;

    var isSwitchedOn = togglePinTool(this.annotationPlugin, pinType);

    // Remembered so the creation handler knows what the pin is for even if
    // the tool has already switched itself off when the event arrives.
    this.pendingPinType = isSwitchedOn ? pinType : null;
  };

  /**
   * Registers the commands behind the pin toolbar button and its dropdown.
   */
  registerPinCommands = () => {
    registerPinCommands({
      commandsPlugin: this.commandsPlugin,
      uiPlugin: this.uiPlugin,
      pinTypes: PIN_DROP_TYPES,
      onTogglePinTool: this.togglePinTool,
    });
  };

  /**
   * Shows the pin button beside the select tool, or removes it when pins
   * may not be dropped (see canDropPins).
   *
   * @param {Object} props - component props to evaluate
   */
  updatePinToolbar = (props) => {
    var canDropPins = this.canDropPins(props);

    updatePinToolbar({
      uiPlugin: this.uiPlugin,
      pinTypes: PIN_DROP_TYPES,
      isEnabled: canDropPins,
    });

    // Leaving an editable mode with a pin tool armed must not leave it armed.
    if (!canDropPins && this.getActivePinType()) {
      this.pendingPinType = null;
      this.annotationPlugin.setActiveTool(null);
    }
  };

  /**
   * Subscribes to selection plugin events to track current selection state.
   * Equivalent to PDFTron's textSelected event.
   */
  subscribeToSelectionEvents = () => {
    if (!this.selectionPlugin) return;

    var _that = this;

    this.selectionPlugin.onSelectionChange(function (event) {
      if (event.selection) {
        _that.setState({
          currentPageIndex: event.selection.start.page,
        });
        // The text only arrives (via onTextRetrieved) when it is asked for.
        // Comment and reference captions are taken from it.
        _that.selectionPlugin.getSelectedText();
      }

      // Handle deletedDocumentClaim cleanup
      if (_that.state.deletedDocumentClaim && _that.annotationPlugin) {
        var pageIndex = event.selection
          ? event.selection.start.page
          : _that.state.currentPageIndex;
        if (pageIndex != null) {
          _that.annotationPlugin.deleteAnnotation(
            pageIndex,
            _that.state.deletedDocumentClaim
          );
        }
      }
    });

    this.selectionPlugin.onTextRetrieved(function (event) {
      if (event.text) {
        _that.setState({
          currentAnnotationText: event.text.join(""),
        });
      }
    });
  };

  /**
   * Subscribes to annotation plugin state changes to detect when a user
   * clicks (selects/deselects) an annotation. Mirrors PDFTron's
   * annotationSelected event handler from PDFDocument.js.
   */
  subscribeToAnnotationEvents = () => {
    if (!this.annotationPlugin) return;

    var _that = this;
    var previousSelectedUid = null;

    this.annotationPlugin.onStateChange(function (stateEvent) {
      var state = stateEvent.state || stateEvent;
      var selectedUid = state.selectedUid;

      // No change in selection
      if (selectedUid === previousSelectedUid) return;

      var wasSelected = previousSelectedUid;
      previousSelectedUid = selectedUid;

      // A deselect this component caused itself (see silentDeselectId)
      var isOwnDeselect = wasSelected && wasSelected === _that.silentDeselectId;
      if (isOwnDeselect) {
        _that.silentDeselectId = null;
      }

      var pendingShapeId = _that.pendingShape ? _that.pendingShape.id : null;

      // The shape waiting for Comment / Reference was selected: its popup
      // takes it from here. It has no conversation or reference to open.
      if (selectedUid && selectedUid === pendingShapeId) return;

      // The user moved on from a shape without choosing: it is abandoned.
      if (wasSelected && wasSelected === pendingShapeId) {
        var isSettling =
          Date.now() - _that.pendingShape.drawnAt < SHAPE_INTENT_SETTLE_MS;
        if (!isSettling) {
          _that.discardPendingShape();
        }
        if (!selectedUid) return;
      }

      if (!selectedUid && isOwnDeselect) return;

      if (selectedUid) {
        // Annotation selected
        var tracked = state.byUid[selectedUid];
        if (!tracked) return;

        var annotation = tracked.object;
        var annotationId = annotation.id;
        var pageIndex = annotation.pageIndex;

        // Store in localStorage (matching PDFDocument behavior)
        localStorage.setItem("parentDocumentId", _that.props.docId);
        localStorage.setItem("annotationHighlightXfdfId", annotationId);
        localStorage.setItem(
          "parentAnnotationHighlightXfdfPageNumber",
          parseInt(pageIndex) + 1
        );

        // Determine if this is a reference annotation by checking
        // the referenceAnnotationIds array (populated during import)
        var isReference = _that.isReferenceAnnotation(annotationId);

        // Call parent handler
        if (_that.props.onAnnotationSelect) {
          _that.props.onAnnotationSelect(pageIndex, annotationId, isReference);
        }
      } else if (wasSelected) {
        // Annotation deselected
        if (_that.props.onAnnotationDeselect) {
          _that.props.onAnnotationDeselect();
        }
      }
    });
  };

  /**
   * Checks if an annotation type is a shape (drawn via tool, not text markup).
   * PdfAnnotationSubtype: LINE=4, SQUARE=5, CIRCLE=6, POLYGON=7, POLYLINE=8, INK=15
   */
  isShapeAnnotationType = (type) => {
    return isShapeAnnotationType(type);
  };

  /**
   * Checks if an annotation belongs to a document reference.
   * Uses the referenceAnnotationIds array that is populated during
   * annotation import in importDocumentReferencesPaperView.
   *
   * @param {string} annotationId - The annotation ID to check
   * @returns {boolean} True if this annotation is a document reference
   */
  isReferenceAnnotation = (annotationId) => {
    var refIds = this.state.referenceAnnotationIds;
    for (var i = 0; i < refIds.length; i++) {
      if (refIds[i] == annotationId) {
        return true;
      }
    }
    return false;
  };

  /**
   * Checks if an annotation belongs to a conversation (comment).
   * Uses the conversationAnnotationIds array populated during import
   * and when creating new comments via pickCommentPaperView.
   *
   * @param {string} annotationId - The annotation ID to check
   * @returns {boolean} True if this annotation is a conversation/comment
   */
  isConversationAnnotation = (annotationId) => {
    var convoIds = this.state.conversationAnnotationIds;

    for (var i = 0; i < convoIds.length; i++) {
      if (convoIds[i] == annotationId) {
        return true;
      }
    }

    return false;
  };

  /**
   * Checks if an annotation belongs to a claim.
   * Uses the claimAnnotationIds array populated during import
   * and when creating new claims via pickClaimPaperView.
   *
   * @param {string} annotationId - The annotation ID to check
   * @returns {boolean} True if this annotation is a claim
   */
  isClaimAnnotation = (annotationId) => {
    var claimIds = this.state.claimAnnotationIds;

    for (var i = 0; i < claimIds.length; i++) {
      if (claimIds[i] == annotationId) {
        return true;
      }
    }

    return false;
  };

  /**
   * Checks if an annotation belongs to a signature.
   * Uses the signatures prop passed from Viewer.js.
   *
   * @param {string} annotationId - The annotation ID to check
   * @returns {boolean} True if this annotation is a signature
   */
  isSignatureAnnotation = (annotationId) => {
    if (!this.props.signatures) {
      return false;
    }

    for (var i = 0; i < this.props.signatures.length; i++) {
      if (this.props.signatures[i].annotation_id === annotationId) {
        return true;
      }
    }

    return false;
  };

  /**
   * Checks if an annotation is a Papercurve-created annotation.
   * Papercurve annotations include: references, conversations, claims, signatures.
   * Native PDF annotations (embedded in the PDF file) are NOT Papercurve annotations.
   *
   * Used by hideNativeAnnotationsPaperView to identify which annotations
   * are native (should be hidden) vs Papercurve (should remain visible).
   *
   * @param {string} annotationId - The annotation ID to check
   * @returns {boolean} True if this is a Papercurve annotation
   */
  isPapercurveAnnotation = (annotationId) => {
    return (
      this.isReferenceAnnotation(annotationId) ||
      this.isConversationAnnotation(annotationId) ||
      this.isClaimAnnotation(annotationId) ||
      this.isSignatureAnnotation(annotationId)
    );
  };

  // ===========================================
  // Annotation Lifecycle: Update + Delete Helpers
  // ===========================================

  /**
   * Debounces annotation update events per annotation ID.
   * PaperView PDF fires multiple update events during a single drag.
   * This ensures only the final position is exported and sent to the
   * backend, avoiding redundant network requests.
   */
  _pendingUpdateTimers = {};

  debouncedAnnotationUpdate = (event, annotation) => {
    var annotationId = annotation.id;
    var _that = this;

    if (this._pendingUpdateTimers[annotationId]) {
      clearTimeout(this._pendingUpdateTimers[annotationId]);
    }

    this._pendingUpdateTimers[annotationId] = setTimeout(function () {
      delete _that._pendingUpdateTimers[annotationId];

      // The uncommitted update event carries the PRE-patch annotation object
      // (the new values live only in event.patch), so exporting from the
      // event serializes the OLD geometry — moves were saved at their old
      // position. Export from current plugin state instead: by the time the
      // debounce fires it holds the final position.
      var annotationForExport = annotation;
      var state = _that.annotationPlugin && _that.annotationPlugin.getState();
      var tracked = state && state.byUid && state.byUid[annotationId];
      if (tracked && tracked.object) {
        annotationForExport = tracked.object;
      }

      // A shape or pin whose comment or reference has not been saved yet has
      // nothing to update on the backend: refresh the XFDF waiting to be
      // saved instead, so it is saved where the user left it.
      var isUnsavedDrawnAnnotation =
        _that.drawnAnnotationIds[annotationId] &&
        !_that.isSavedAnnotation(annotationId);
      if (isUnsavedDrawnAnnotation) {
        _that.refreshUnsavedAnnotationXfdf(annotationForExport, event);
        return;
      }
      delete _that.drawnAnnotationIds[annotationId];

      var exportEvent = {
        type: "update",
        annotation: annotationForExport,
        pageIndex: event.pageIndex,
        documentId: event.documentId,
        committed: event.committed,
      };

      var xfdf = _that.xfdfPlugin
        ? _that.xfdfPlugin.exportAnnotationEvent(exportEvent)
        : "";
      _that.saveAnnotationUpdate(annotationForExport, xfdf);
    }, 300);
  };

  /**
   * Tells whether an annotation already belongs to a saved conversation or
   * reference. Both lists are refetched from the backend after a comment is
   * saved or a reference is linked.
   *
   * @param {string} annotationId - annotation id (xfdf_highlight_id)
   * @returns {boolean}
   */
  isSavedAnnotation = (annotationId) => {
    var hasAnnotation = function (records) {
      if (!records || !records.length) return false;

      return records.some(function (record) {
        return record.xfdf_highlight_id === annotationId;
      });
    };

    return (
      hasAnnotation(this.props.documentConversations) ||
      hasAnnotation(this.props.documentReferences)
    );
  };

  /**
   * Re-exports a drawn annotation that is still waiting for its comment or
   * reference to be saved, after the user moved or resized it.
   *
   * The XFDF captured when the annotation was drawn is what gets saved
   * later: the comment flow reads it from Viewer state and the reference
   * flow from localStorage. Without this refresh the annotation is saved at
   * the spot where it was first drawn.
   *
   * @param {Object} annotation - the annotation's current object from
   *   plugin state (already at its new position)
   * @param {Object} event - the annotation plugin's update event
   */
  refreshUnsavedAnnotationXfdf = (annotation, event) => {
    if (!this.xfdfPlugin) return;

    // Exported as a creation (<add>), the same form it had when first drawn.
    var xfdf = this.xfdfPlugin.exportAnnotationEvent({
      type: "create",
      annotation: annotation,
      pageIndex: event.pageIndex,
      documentId: event.documentId,
      committed: false,
    });
    if (!xfdf) return;

    var annotationId = annotation.id;

    if (this.state.currentAnnotation === annotationId) {
      this.setState({ currentAnnotationXfdf: xfdf });
    }

    if (this.props.updateAnnotationXfdf) {
      this.props.updateAnnotationXfdf({ id: annotationId, xfdf: xfdf });
    }

    localStorage.setItem(annotationId, xfdf);
  };

  /**
   * Persists an annotation modification (move, resize) to the backend.
   * Determines the annotation type by looking up the annotation ID in
   * backend data (signatures, referenceAnnotationIds), then routes to
   * the correct API endpoint:
   *   - Signatures  → PUT /document_signatures/{dbId}.json
   *   - Strikeouts  → PUT /conversations/xfdf/{id}.json
   *   - References  → PUT /document/references/{id}.json  (skipped in redefine mode)
   *   - Comments/shapes → PUT /conversations/xfdf/{id}.json (skipped in redefine mode)
   */
  saveAnnotationUpdate = (annotation, xfdf) => {
    var annotationId = annotation.id;

    // Signature annotations — matched by checking props.signatures array
    if (this.props.signatures) {
      var signature = this.props.signatures.find(function (sig) {
        return sig.annotation_id === annotationId;
      });
      if (signature) {
        httpClient
          .put("/document_signatures/" + signature.id + ".json", {
            annotation_xfdf: xfdf,
          })
          .catch(function (error) {
            console.error(error);
          });
        return;
      }
    }

    // Strikeout annotations — always save regardless of redefine mode
    if (annotation.type === 12) {
      httpClient
        .put("/conversations/xfdf/" + annotationId + ".json", { xfdf: xfdf })
        .catch(function (error) {
          console.error(error);
        });
      return;
    }

    // Skip saving in redefine mode — XFDF is stored in state
    // and committed only when the user clicks "Apply Changes"
    if (this.props.inRedefineMode) return;

    // Reference annotations — matched by checking referenceAnnotationIds
    // (populated during import in importDocumentReferencesPaperView)
    var isReference = this.isReferenceAnnotation(annotationId);
    if (isReference) {
      httpClient
        .put("/document/references/" + annotationId + ".json", { xfdf: xfdf })
        .catch(function (error) {
          console.error(error);
        });
      return;
    }

    // Comments, shapes (rectangle, polygon, line, etc.)
    httpClient
      .put("/conversations/xfdf/" + annotationId + ".json", { xfdf: xfdf })
      .catch(function (error) {
        console.error(error);
      });
  };

  /**
   * Handles annotation deletion events. Currently scoped to:
   *   - Document claims (squiggly, type 13): flags for cleanup on next selection
   *   - Signatures: DELETE /document_signatures/{id}.json
   *
   * Strikeout/comment/reference deletion is handled separately via UI panels.
   */
  handleAnnotationDelete = (annotation) => {
    var annotationId = annotation.id;

    // Document claims (squiggly) — flag for cleanup on next selection
    if (annotation.type === 13) {
      this.setState({ deletedDocumentClaim: annotationId });
      return;
    }

    // Signature annotations
    if (this.props.signatures) {
      var _that = this;
      var signature = this.props.signatures.find(function (sig) {
        return sig.annotation_id === annotationId;
      });
      if (signature) {
        httpClient
          .delete("/document_signatures/" + signature.id + ".json")
          .then(function () {
            if (_that.props.loadSignatures) {
              _that.props.loadSignatures();
            }
          })
          .catch(function (error) {
            console.error(error);
          });
      }
    }
  };

  // ===========================================
  // Annotation Layer Visibility Toggles (PaperView PDF)
  // ===========================================
  //
  // PaperView PDF has no hideAnnotation()/showAnnotation() API like PDFTron.
  // Instead, we use a delete-to-hide / re-import-to-show approach:
  //
  //   HIDE: Delete annotations from PaperView PDF state (removes from render)
  //   SHOW: Re-import annotations from backend XFDF data
  //
  // The toggleHiddenAnnotationIds guard prevents delete events from triggering
  // API calls (e.g., DELETE /document_signatures) when we're just hiding.

  /**
   * Deletes a list of annotation IDs from the PaperView PDF viewer.
   * Used by visibility toggles to "hide" annotations.
   *
   * IMPORTANT: Marks each ID in toggleHiddenAnnotationIds before deleting.
   * This prevents the delete event handler (handleAnnotationDelete) from
   * sending API DELETE calls — we're just hiding, not actually deleting.
   *
   * @param {string[]} annotationIds - Array of annotation IDs to delete/hide
   */
  deleteAnnotationsByIds = (annotationIds) => {
    if (!this.annotationPlugin) {
      return;
    }

    var state = this.annotationPlugin.getState();

    if (!state || !state.byUid) {
      return;
    }

    for (var i = 0; i < annotationIds.length; i++) {
      var id = annotationIds[i];
      var tracked = state.byUid[id];

      // Only delete if the annotation exists and isn't already deleted
      if (tracked && tracked.commitState !== "deleted") {
        // Mark as toggle-hidden so event handlers skip this deletion
        this.toggleHiddenAnnotationIds[id] = true;

        // Delete from PaperView PDF (removes from render)
        this.annotationPlugin.deleteAnnotation(tracked.object.pageIndex, id);
      }
    }
  };

  /**
   * Hides all comment annotations (conversations that are not refs/claims).
   * Deletes from PaperView PDF — to show again, call showAllCommentsPaperView.
   *
   * Comments are identified by:
   * 1. IDs in conversationAnnotationIds (tracked during import/creation)
   * 2. IDs in documentConversations Redux state (may have newer data)
   *
   * We exclude references and claims since they have their own toggles.
   */
  hideAllCommentsPaperView = () => {
    var commentIds = [];

    // Step 1: Collect IDs from conversationAnnotationIds (local tracking)
    var convoIds = this.state.conversationAnnotationIds;

    for (var i = 0; i < convoIds.length; i++) {
      var id = convoIds[i];

      // Exclude references and claims — they have separate toggles
      var isReference = this.isReferenceAnnotation(id);
      var isClaim = this.isClaimAnnotation(id);

      if (!isReference && !isClaim) {
        commentIds.push(id);
      }
    }

    // Step 2: Also check Redux state (may have IDs not yet in local state)
    if (this.props.documentConversations) {
      for (var j = 0; j < this.props.documentConversations.length; j++) {
        var convoId = this.props.documentConversations[j].xfdf_highlight_id;

        // Skip if no ID, already collected, or is a reference/claim
        var alreadyCollected = commentIds.indexOf(convoId) !== -1;
        var isRef = this.isReferenceAnnotation(convoId);
        var isClm = this.isClaimAnnotation(convoId);

        if (convoId && !alreadyCollected && !isRef && !isClm) {
          commentIds.push(convoId);
        }
      }
    }

    this.deleteAnnotationsByIds(commentIds);
  };

  /**
   * Shows all comment annotations by re-importing from backend data.
   * This reverses hideAllCommentsPaperView by re-creating deleted annotations.
   */
  showAllCommentsPaperView = () => {
    this.reloadConversationsPaperView(this.props.documentConversations);
  };

  /**
   * Hides all reference annotations.
   * Deletes from PaperView PDF — to show again, call showAllReferencesPaperView.
   */
  hideAllReferencesPaperView = () => {
    this.deleteAnnotationsByIds(this.state.referenceAnnotationIds);
  };

  /**
   * Shows all reference annotations by re-importing from backend data.
   * This reverses hideAllReferencesPaperView by re-creating deleted annotations.
   */
  showAllReferencesPaperView = () => {
    this.reloadReferencesPaperView(this.props.documentReferences);
  };

  /**
   * Hides all claim annotations (both claims and documentClaims).
   * Deletes from PaperView PDF — to show again, call showAllClaimsPaperView.
   */
  hideAllClaimsPaperView = () => {
    this.deleteAnnotationsByIds(this.state.claimAnnotationIds);
  };

  /**
   * Shows all claim annotations by re-importing from backend data.
   * This reverses hideAllClaimsPaperView by re-creating deleted annotations.
   * Imports from both documentClaims and claims props.
   */
  showAllClaimsPaperView = async () => {
    await this.reloadClaimsPaperView(this.props.documentClaims);
    await this.reloadClaimsPaperView(this.props.claims);
  };

  /**
   * Hides native PDF annotations (annotations embedded in the PDF file).
   * Native annotations are NOT Papercurve annotations — they existed in the
   * PDF before any Papercurve interactions.
   *
   * Unlike Papercurve annotations (which can be re-imported from backend XFDF),
   * native annotations have no backend storage. We must snapshot them before
   * deleting so they can be restored via showNativeAnnotationsPaperView.
   *
   * The snapshot stores a deep copy of each annotation object.
   */
  hideNativeAnnotationsPaperView = () => {
    if (!this.annotationPlugin) {
      return;
    }

    var state = this.annotationPlugin.getState();

    if (!state || !state.byUid) {
      return;
    }

    // Step 1: Build snapshot of native annotations before deleting
    var snapshot = [];
    var uids = Object.keys(state.byUid);

    for (var i = 0; i < uids.length; i++) {
      var id = uids[i];
      var tracked = state.byUid[id];

      // Skip already-deleted annotations
      if (tracked.commitState === "deleted") {
        continue;
      }

      // Only snapshot non-Papercurve (native) annotations
      if (!this.isPapercurveAnnotation(id)) {
        snapshot.push({
          id: id,
          pageIndex: tracked.object.pageIndex,
          object: Object.assign({}, tracked.object), // Deep copy
        });
      }
    }

    // Step 2: Save snapshot to state (for later restore)
    this.setState({ nativeAnnotationSnapshot: snapshot });

    // Step 3: Delete the native annotations from the viewer
    var nativeIds = [];

    for (var j = 0; j < snapshot.length; j++) {
      nativeIds.push(snapshot[j].id);
    }

    this.deleteAnnotationsByIds(nativeIds);
  };

  /**
   * Shows native PDF annotations by re-creating them from the snapshot.
   * This reverses hideNativeAnnotationsPaperView.
   *
   * Unlike Papercurve annotations (which re-import from backend), native
   * annotations are restored from the in-memory snapshot taken when hiding.
   */
  showNativeAnnotationsPaperView = () => {
    if (!this.annotationPlugin) {
      return;
    }

    var snapshot = this.state.nativeAnnotationSnapshot;

    // Re-create each annotation from the snapshot
    for (var i = 0; i < snapshot.length; i++) {
      var entry = snapshot[i];
      this.annotationPlugin.createAnnotation(entry.pageIndex, entry.object);
    }

    // Clear the snapshot after restoring
    this.setState({ nativeAnnotationSnapshot: [] });
  };

  /**
   * Subscribes to annotation lifecycle events (create, update, delete).
   *
   * CREATE: When a user draws a shape, open a comment panel.
   * UPDATE: When a user moves/resizes an annotation, persist to backend.
   * DELETE: When a user deletes an annotation, sync with backend.
   *
   * Uses exportAnnotationEvent() for per-annotation command-format XFDF
   * instead of exportXfdf() which dumps the entire document.
   * Mirrors PDFDocument's annotationChanged handler.
   */
  subscribeToAnnotationLifecycleEvents = () => {
    if (!this.annotationPlugin) return;

    var _that = this;

    // Snapshot all annotation IDs that exist right now (from XFDF import).
    // These should be ignored — only user-drawn shapes should trigger the handler.
    var importedAnnotationIds = {};
    var currentState = this.annotationPlugin.getState();
    if (currentState && currentState.byUid) {
      var uids = Object.keys(currentState.byUid);
      for (var i = 0; i < uids.length; i++) {
        importedAnnotationIds[uids[i]] = true;
      }
    }

    this.annotationPlugin.onAnnotationEvent(function (event) {
      var annotation = event.annotation;
      if (!annotation) return;

      // Skip commit echoes. The plugin re-emits create/update/delete events
      // with committed=true once the engine persists a user action; only the
      // original committed=false event represents the action itself.
      // Processing the create echo re-ran the "close the previous
      // conversation" path below, which deleted the shape just drawn.
      if (event.committed) return;

      // Skip annotations being hidden via visibility toggles — the hide is
      // a viewer-side delete that must NOT sync to the backend. The flag is
      // consumed by that delete event: each hide dispatches exactly one, and
      // leaving the flag set silenced every later event for the annotation
      // (moves after a hide/show toggle were never saved).
      if (_that.toggleHiddenAnnotationIds[annotation.id]) {
        if (event.type === "delete") {
          delete _that.toggleHiddenAnnotationIds[annotation.id];
        }
        return;
      }

      // Save annotation changes (move, resize) to the backend.
      // Debounced per annotation — only the final position is sent.
      if (event.type === "update") {
        _that.debouncedAnnotationUpdate(event, annotation);
        return;
      }

      // Handle annotation deletion (signatures, document claims).
      if (event.type === "delete") {
        _that.handleAnnotationDelete(annotation);
        return;
      }

      if (event.type !== "create") return;

      // Skip annotations that existed before subscription (imported via XFDF)
      if (importedAnnotationIds[annotation.id]) return;

      // Only handle annotations placed with a drawing tool: shapes and pins.
      // Pick method annotations (HIGHLIGHT type 9, STRIKEOUT type 12) are
      // filtered out — they are text markup, not drawn.
      var isPin = isPinAnnotation(annotation);
      var isShape = _that.isShapeAnnotationType(annotation.type);
      if (!isShape && !isPin) return;

      var annotationId = annotation.id;
      var pageIndex = event.pageIndex;

      _that.drawnAnnotationIds[annotationId] = true;

      // A shape is drawn first and its purpose chosen after, from a popup.
      if (isShape) {
        _that.offerShapeIntent(annotationId, pageIndex);
        return;
      }

      // A pin's purpose comes from the pin tool it was dropped with.
      var pinType =
        _that.getActivePinType() || _that.pendingPinType || PIN_TYPES.COMMENT;
      _that.pendingPinType = null;

      _that.leaveSelectedAnnotation();

      // Export per-annotation XFDF in command format (<add>...</add>)
      var xfdfString = "";
      if (_that.xfdfPlugin) {
        xfdfString = _that.xfdfPlugin.exportAnnotationEvent(event);
      }

      if (pinType === PIN_TYPES.REFERENCE) {
        _that.startReferenceOnDrawnAnnotation(
          annotationId,
          pageIndex,
          xfdfString
        );
      } else {
        _that.startCommentOnDrawnAnnotation(
          annotationId,
          pageIndex,
          xfdfString
        );
      }
    });
  };

  /**
   * Tells the parent that whatever annotation was selected before is being
   * left, before a drawn shape or pin starts its own comment or reference.
   *
   * Selecting text deselects the previous annotation first, and the parent
   * resets its panel state on that deselect. Drawing does not deselect
   * anything, so without this the parent still holds the previous
   * annotation: its reference panel stayed open over the new comment panel,
   * and the comment flow's "close the previous conversation" step removed
   * that previous, saved, annotation from the page.
   */
  leaveSelectedAnnotation = () => {
    if (this.props.onAnnotationDeselect) {
      this.props.onAnnotationDeselect();
    }
  };

  /**
   * Shows the Comment / Reference popup on a shape that was just drawn.
   * Nothing is opened or saved until the user chooses (chooseShapeIntent);
   * a shape left without a choice is removed (discardPendingShape).
   *
   * The popup is Paperview PDF's annotation selection menu, so the shape is
   * selected to bring it up, and the drawing tool is switched off so the
   * shape and its popup can be clicked.
   *
   * @param {string} annotationId - id of the shape just drawn
   * @param {number} pageIndex - zero-based page the shape is on
   */
  offerShapeIntent = (annotationId, pageIndex) => {
    var _that = this;

    // Only one shape waits for a choice at a time.
    this.discardPendingShape();

    this.pendingShape = {
      id: annotationId,
      pageIndex: pageIndex,
      drawnAt: Date.now(),
    };

    if (this.uiPlugin) {
      this.uiPlugin.enableCategory(SHAPE_INTENT_MENU_CATEGORY);
    }

    if (!this.annotationPlugin) return;

    this.annotationPlugin.setActiveTool(null);

    // Selected after the drawing gesture has finished, so the gesture's own
    // pointer handling does not undo the selection.
    setTimeout(function () {
      var isStillPending =
        _that.pendingShape && _that.pendingShape.id === annotationId;
      if (isStillPending && _that.annotationPlugin) {
        _that.annotationPlugin.selectAnnotation(pageIndex, annotationId);
      }
    }, 0);
  };

  /**
   * Hides the Comment / Reference popup and forgets the shape that was
   * waiting for the choice. The shape itself is left as it is.
   *
   * @returns {{ id: string, pageIndex: number }|null} the shape that was waiting
   */
  clearPendingShape = () => {
    var pendingShape = this.pendingShape;
    this.pendingShape = null;

    if (this.uiPlugin) {
      this.uiPlugin.disableCategory(SHAPE_INTENT_MENU_CATEGORY);
    }

    return pendingShape;
  };

  /**
   * Removes a drawn shape the user left without choosing Comment or
   * Reference, the same way an abandoned comment's shape is removed.
   */
  discardPendingShape = () => {
    var pendingShape = this.clearPendingShape();
    if (!pendingShape) return;

    delete this.drawnAnnotationIds[pendingShape.id];
    this.silentDeselectId = pendingShape.id;
    this.deleteAnnotationById(pendingShape.id);
  };

  /**
   * Handles the user's choice in a drawn shape's popup: starts a comment or
   * a reference on the shape, through the same flows a pin uses.
   *
   * The XFDF is exported from the shape's current state, so a shape the user
   * moved or resized before choosing is saved as it is now.
   *
   * @param {string} intent - a SHAPE_INTENTS value
   */
  chooseShapeIntent = (intent) => {
    var pendingShape = this.clearPendingShape();
    if (!pendingShape || !this.annotationPlugin) return;

    var annotationId = pendingShape.id;
    var state = this.annotationPlugin.getState();
    var tracked = state && state.byUid && state.byUid[annotationId];
    if (!tracked || !tracked.object) return;

    var pageIndex = tracked.object.pageIndex;

    // Export per-annotation XFDF in command format (<add>...</add>)
    var xfdfString = "";
    if (this.xfdfPlugin) {
      xfdfString = this.xfdfPlugin.exportAnnotationEvent({
        type: "create",
        annotation: tracked.object,
        pageIndex: pageIndex,
        documentId: undefined,
        committed: false,
      });
    }

    // The shape stays on the page but is deselected: the side panel takes
    // over, and clicking elsewhere must not read as leaving the annotation.
    this.silentDeselectId = annotationId;
    this.annotationPlugin.deselectAnnotation();

    this.leaveSelectedAnnotation();

    if (intent === SHAPE_INTENTS.REFERENCE) {
      this.startReferenceOnDrawnAnnotation(annotationId, pageIndex, xfdfString);
    } else {
      this.startCommentOnDrawnAnnotation(annotationId, pageIndex, xfdfString);
    }
  };

  /**
   * Starts a comment on an annotation that was placed with a drawing tool
   * (a shape or a comment pin). Mirrors pickCommentPaperView, minus the text
   * selection: the annotation already exists.
   *
   * @param {string} annotationId - id of the drawn annotation
   * @param {number} pageIndex - zero-based page the annotation is on
   * @param {string} xfdfString - the annotation's XFDF in command format
   */
  startCommentOnDrawnAnnotation = (annotationId, pageIndex, xfdfString) => {
    this.setState({
      requestedAnnotation: "comment",
      currentAnnotation: annotationId,
      currentAnnotationXfdf: xfdfString,
      currentPageNumber: parseInt(pageIndex) + 1,
    });

    // Close competing panels
    if (this.props.closeDocumentReferencePanel)
      this.props.closeDocumentReferencePanel();
    if (this.props.closeReferenceDocumentPanel)
      this.props.closeReferenceDocumentPanel();
    if (this.props.closeConversation) this.props.closeConversation();
    if (this.props.closeAttachmentsPanel) this.props.closeAttachmentsPanel();

    // Initialize conversation state — same logic as pickCommentPaperView.
    // If a previous conversation panel is open, close it and track the
    // previous annotation ID. Otherwise mark this as the first conversation.
    if (
      this.props.conversationsInitiated &&
      this.props.conversationsInitiated.length >= 1
    ) {
      if (this.props.onCloseConversation)
        this.props.onCloseConversation(true, true);
      if (this.props.setPreviousConvoId)
        this.props.setPreviousConvoId(annotationId);
    } else {
      if (this.props.setConversationsInitiated)
        this.props.setConversationsInitiated("latest", false);
      if (this.props.setPreviousConvoId)
        this.props.setPreviousConvoId(annotationId);
    }

    // Store annotation in Viewer.js state (addedAnnotations map) so
    // CommentPanel knows this is new and skips the conversation fetch.
    if (this.props.addAnnotation) {
      this.props.addAnnotation({
        id: annotationId,
        xfdf: xfdfString,
      });
    }
    this.storeAnnotationInLocalStorage(annotationId, xfdfString, pageIndex);

    // Trigger comment panel in parent Viewer
    if (this.props.onTextSelection) {
      this.props.onTextSelection(
        "comment",
        pageIndex,
        annotationId,
        xfdfString
      );
    }
  };

  /**
   * Starts linking a reference from an annotation that was just placed with
   * a drawing tool (a shape or a reference pin). Mirrors pickReferencePaperView, minus
   * the text selection: the annotation already exists and has no text, so
   * the reference caption starts empty.
   *
   * @param {string} annotationId - id of the annotation just created
   * @param {number} pageIndex - zero-based page the annotation is on
   * @param {string} xfdfString - the annotation's XFDF in command format
   */
  startReferenceOnDrawnAnnotation = (annotationId, pageIndex, xfdfString) => {
    if (this.props.hideClaimsPanel) this.props.hideClaimsPanel();
    if (this.props.hideLinkClaimPanel) this.props.hideLinkClaimPanel();

    this.setState({
      requestedAnnotation: "reference",
      currentAnnotation: annotationId,
      currentAnnotationXfdf: xfdfString,
      currentPageNumber: parseInt(pageIndex) + 1,
      currentAnnotationText: "",
    });

    // Open reference panels and close competing ones
    if (this.props.closeConversation) this.props.closeConversation();
    if (this.props.closeAttachmentsPanel) this.props.closeAttachmentsPanel();
    if (this.props.openReferenceDocumentPanel)
      this.props.openReferenceDocumentPanel();
    if (this.props.linkingReferences) this.props.linkingReferences();
    if (this.props.closeDocumentReferencePanel)
      this.props.closeDocumentReferencePanel();
    if (this.props.setCurrentAnnotationText)
      this.props.setCurrentAnnotationText("");

    if (this.props.addAnnotation) {
      this.props.addAnnotation({
        id: annotationId,
        xfdf: xfdfString,
        type: "reference",
      });
    }
    this.storeAnnotationInLocalStorage(annotationId, xfdfString, pageIndex);
    // storeAnnotationInLocalStorage keeps the last selected text as the
    // caption fallback; a pin has no text, so it must not inherit that.
    localStorage.setItem("currentAnnotationText", "");

    if (this.props.onTextSelection) {
      this.props.onTextSelection(
        "reference",
        pageIndex,
        annotationId,
        xfdfString
      );
    }
  };

  /**
   * Creates an annotation from the current text selection.
   * Used by all four pick methods (comment, reference, strikeout, claim).
   *
   * @param {string} type - "comment", "reference", "strikeout", or "claim"
   *   Used by the calling pick method to route to the correct panel/API.
   *   Determines annotation subtype (strikeout vs highlight).
   * @param {string} color - Hex color for the highlight (e.g. "#FFCC99")
   * @param {number} opacity - Opacity value (0-1)
   * @returns {{ annotationId, pageIndex, xfdf, rect } | null}
   *   Returns null if there's no active text selection.
   *   rect uses {left, top, right, bottom} for the server/API format.
   */
  createAnnotationFromSelection = async (type, color, opacity) => {
    if (!this.selectionPlugin || !this.annotationPlugin) return null;

    var selectionState = this.selectionPlugin.getState();
    if (!selectionState || !selectionState.selection) return null;

    var pageIndex = selectionState.selection.start.page;
    var rects = this.selectionPlugin.getHighlightRectsForPage(pageIndex);
    if (!rects || rects.length === 0) return null;

    // Compute the bounding rect that encompasses all selected text segments.
    // Each rect from the selection plugin is in PaperView PDF's native format:
    // {origin: {x, y}, size: {width, height}}
    var left = rects[0].origin.x;
    var top = rects[0].origin.y;
    var right = rects[0].origin.x + rects[0].size.width;
    var bottom = rects[0].origin.y + rects[0].size.height;
    for (var i = 1; i < rects.length; i++) {
      var r = rects[i];
      if (r.origin.x < left) left = r.origin.x;
      if (r.origin.y < top) top = r.origin.y;
      if (r.origin.x + r.size.width > right) right = r.origin.x + r.size.width;
      if (r.origin.y + r.size.height > bottom)
        bottom = r.origin.y + r.size.height;
    }

    // PdfAnnotationSubtype enum values used by PaperView PDF
    var annotationType = type === "strikeout" ? 12 : 9; // STRIKEOUT=12, HIGHLIGHT=9

    // Generate a human-readable ID before creating — PaperView PDF does NOT
    // auto-generate IDs (unlike its internal onEndSelection which calls uuidV4).
    var annotationId = generateAnnotationId(
      this.props.docId,
      security.getUserId(),
      pageIndex
    );

    // PaperView PDF requires rect in {origin, size} format (NOT {left, top, right, bottom}).
    // segmentRects are the individual text line rects — passed directly from the selection
    // plugin since they're already in the correct format.
    var annotation = {
      id: annotationId,
      type: annotationType,
      pageIndex: pageIndex,
      rect: {
        origin: { x: left, y: top },
        size: { width: right - left, height: bottom - top },
      },
      color: color || "#FFCC99",
      opacity: opacity != null ? opacity : 0.5,
      segmentRects: rects,
      created: new Date(),
    };

    this.annotationPlugin.createAnnotation(pageIndex, annotation);

    // Export per-annotation XFDF in command format using a synthetic event.
    // exportAnnotationEvent reads event.type, event.annotation, and event.pageIndex.
    var xfdfString = "";
    if (this.xfdfPlugin) {
      try {
        xfdfString = this.xfdfPlugin.exportAnnotationEvent({
          type: "create",
          annotation: annotation,
          pageIndex: pageIndex,
          documentId: undefined,
          committed: false,
        });
      } catch (e) {
        // XFDF export failed — annotation still created visually
      }
    }

    this.selectionPlugin.clear();

    // Return {left, top, right, bottom} rect format for the server API
    // (used by claim's xfdf_coords field)
    return {
      annotationId: annotationId,
      pageIndex: pageIndex,
      xfdf: xfdfString,
      rect: { left: left, top: top, right: right, bottom: bottom },
    };
  };

  /**
   * Deletes an annotation by ID. Called by parent (Viewer) when it needs
   * to remove an annotation (e.g. closing claim panel without selecting a claim).
   */
  deleteAnnotationById = (annotationId) => {
    if (!this.annotationPlugin || !annotationId) return;

    var state = this.annotationPlugin.getState();
    if (!state || !state.byUid) return;

    var tracked = state.byUid[annotationId];
    if (tracked && tracked.object && tracked.commitState !== "deleted") {
      this.annotationPlugin.deleteAnnotation(
        tracked.object.pageIndex,
        annotationId
      );
    }
  };

  /**
   * Visually selects an annotation in the viewer and scrolls to its page.
   * Called by parent (Viewer) when a comment, claim or reference card is
   * clicked in a side panel.
   *
   * @param {string} annotationId - XFDF highlight ID of the annotation.
   * @returns {boolean} True if the annotation was found and selected.
   */
  selectAnnotationById = (annotationId) => {
    if (!this.annotationPlugin || !annotationId) return false;

    var state = this.annotationPlugin.getState();
    if (!state || !state.byUid) return false;

    var tracked = state.byUid[annotationId];
    var isSelectable =
      tracked && tracked.object && tracked.commitState !== "deleted";
    if (!isSelectable) return false;

    this.annotationPlugin.selectAnnotation(
      tracked.object.pageIndex,
      annotationId
    );
    this.scrollToPage(tracked.object.pageIndex + 1);
    return true;
  };

  /**
   * Clears any visual annotation selection in the viewer.
   */
  deselectAnnotations = () => {
    if (!this.annotationPlugin) return;
    this.annotationPlugin.deselectAnnotation();
  };

  /**
   * Scrolls the viewer to the specified page number.
   * Called by parent (Viewer) for initial page navigation, finding clicks,
   * and chat citation clicks.
   *
   * @param {number} pageNumber - 1-based page number
   */
  scrollToPage = function (pageNumber) {
    if (this.scrollPlugin) {
      this.scrollPlugin.scrollToPage({
        pageNumber: pageNumber,
        behavior: "instant",
      });
    }
  };

  /**
   * Draws Paige Review findings on the page. Each finding with anchors is
   * outlined on the text it refers to; findings without anchors are kept so
   * the viewer can list them. Pass an empty list to clear them.
   *
   * @param {Array} findings - findings from a PAAB or FDA analysis
   */
  setReviewFindings = (findings) => {
    this.reviewFindings = findings || [];
    this.applyReviewFindings();
  };

  applyReviewFindings = () => {
    if (!this.comparePlugin || !this.documentManagerPlugin) return;

    var viewerDocumentId = this.documentManagerPlugin.getActiveDocumentId();
    // Regions belong to a viewer document; clear the previous one's if the
    // viewer has moved on to another.
    if (
      this.reviewRegionsDocumentId &&
      this.reviewRegionsDocumentId !== viewerDocumentId
    ) {
      this.comparePlugin.setHostRegions(this.reviewRegionsDocumentId, []);
      this.reviewRegionsDocumentId = null;
    }
    if (!viewerDocumentId) return;

    this.comparePlugin.setHostRegions(
      viewerDocumentId,
      findingsToRegions(this.reviewFindings)
    );
    this.reviewRegionsDocumentId = viewerDocumentId;
  };

  /**
   * Selects a finding in the viewer and scrolls to where it is on the page.
   *
   * @param {Object} finding
   * @returns {boolean} false when the viewer does not have the finding
   */
  goToReviewFinding = (finding) => {
    if (!this.comparePlugin || !this.reviewRegionsDocumentId || !finding) {
      return false;
    }
    return this.comparePlugin.goToHostRegion(
      this.reviewRegionsDocumentId,
      String(finding.id)
    );
  };

  /**
   * Returns the total number of pages in the loaded document.
   *
   * @returns {number} Total page count, or 0 if scroll plugin is unavailable
   */
  getTotalPages = function () {
    if (this.scrollPlugin) {
      return this.scrollPlugin.getTotalPages();
    }
    return 0;
  };

  /**
   * Loads a different document version into the viewer at runtime.
   * Closes the current document, opens the new URL via the document-manager
   * plugin, then reloads annotations from the backend for the new version.
   *
   * Called by parent (Viewer.js) when the user switches versions.
   *
   * @param {number|string} docId - Document ID of the version to load
   * @param {string} fileExtension - File extension (e.g. ".pdf")
   */
  loadVersion = function (docId, fileExtension) {
    if (!this.documentManagerPlugin) return;

    var _that = this;
    var token = security.getToken();
    var baseUrl = loadDocumentUrl();
    // PaperView PDF only supports .pdf format
    var extension = ".pdf";
    var url =
      baseUrl + "/documents/" + docId + "/file" + extension + "?token=" + token;

    // The version's PDF rendition may still be generating (office uploads).
    // Wait for readiness before swapping documents; the current version
    // stays visible in the meantime.
    if (this.cancelPdfReadyWait) {
      this.cancelPdfReadyWait();
    }

    this.cancelPdfReadyWait = waitForPdfReady({
      documentId: docId,
      fileUrl: url,
      onReady: function () {
        // Reset annotation tracking state for the new version
        _that.setState({
          referenceAnnotationIds: [],
          conversationAnnotationIds: [],
          claimAnnotationIds: [],
          nativeAnnotationSnapshot: [],
        });

        // Close current document, then open the new version
        _that.documentManagerPlugin
          .closeAllDocuments()
          .toPromise()
          .then(function () {
            return _that.documentManagerPlugin
              .openDocumentUrl({ url: url })
              .toPromise();
          })
          .then(function () {
            // Reload annotations for the new version
            _that.loadAnnotationsPaperView(docId);
          });
      },
      onTimeout: function () {
        notificationService.addNotification(
          "Version not ready",
          "This version is still being processed. Please try again later.",
          "danger"
        );
      },
    });
  };

  /**
   * Stores annotation data in localStorage for cross-document persistence.
   * Mirrors PDFDocument's annotationChanged handler localStorage writes.
   */
  storeAnnotationInLocalStorage = (annotationId, xfdf, pageIndex) => {
    localStorage.setItem("parentDocumentId", this.props.docId);
    localStorage.setItem("annotationHighlightXfdfId", annotationId);
    // Readers send this to the API as page_number, which is one-based.
    localStorage.setItem(
      "parentAnnotationHighlightXfdfPageNumber",
      parseInt(pageIndex) + 1
    );
    localStorage.setItem(
      "currentAnnotationText",
      this.state.currentAnnotationText || ""
    );
    // Store XFDF without modify commands
    var cleanXfdf = xfdf ? xfdf.replace(/<modify>[\s\S]*<\/modify>/, "") : "";
    localStorage.setItem(annotationId, cleanXfdf);
  };

  /**
   * Creates a comment highlight annotation from the current text selection.
   * Color: #FFCC99 (orange/peach). Opens the CommentPanel in Viewer.js.
   *
   * Flow: createAnnotation → close competing panels → init conversation
   * state → store annotation → call onTextSelection("comment") which
   * sets commentXfdfHighlightId in Viewer and mounts the CommentPanel.
   */
  pickCommentPaperView = async () => {
    if (this.props.hideClaimsPanel) this.props.hideClaimsPanel();
    if (this.props.hideLinkClaimPanel) this.props.hideLinkClaimPanel();

    var result = await this.createAnnotationFromSelection(
      "comment",
      "#FFCC99",
      0.5
    );
    if (!result) return;

    // Track the conversation annotation ID for visibility toggles
    var conversationAnnotationIds = this.state.conversationAnnotationIds;
    if (conversationAnnotationIds.indexOf(result.annotationId) === -1) {
      conversationAnnotationIds.push(result.annotationId);
    }

    this.setState({
      requestedAnnotation: "comment",
      currentAnnotation: result.annotationId,
      currentAnnotationXfdf: result.xfdf,
      currentPageNumber: parseInt(result.pageIndex) + 1,
      conversationAnnotationIds: conversationAnnotationIds,
    });

    // Close competing panels
    if (this.props.closeDocumentReferencePanel)
      this.props.closeDocumentReferencePanel();
    if (this.props.closeReferenceDocumentPanel)
      this.props.closeReferenceDocumentPanel();
    if (this.props.closeConversation) this.props.closeConversation();
    if (this.props.closeAttachmentsPanel) this.props.closeAttachmentsPanel();

    // Initialize conversation state
    if (
      this.props.conversationsInitiated &&
      this.props.conversationsInitiated.length >= 1
    ) {
      if (this.props.onCloseConversation)
        this.props.onCloseConversation(true, true);
      if (this.props.setPreviousConvoId)
        this.props.setPreviousConvoId(result.annotationId);
    } else {
      if (this.props.setConversationsInitiated)
        this.props.setConversationsInitiated("latest", false);
      if (this.props.setPreviousConvoId)
        this.props.setPreviousConvoId(result.annotationId);
    }

    // Store annotation and persist to localStorage
    if (this.props.addAnnotation) {
      this.props.addAnnotation({
        id: result.annotationId,
        xfdf: result.xfdf,
      });
    }
    this.storeAnnotationInLocalStorage(
      result.annotationId,
      result.xfdf,
      result.pageIndex
    );

    if (this.props.onTextSelection) {
      this.props.onTextSelection(
        "comment",
        result.pageIndex,
        result.annotationId,
        result.xfdf
      );
    }
  };

  /**
   * Creates a reference highlight annotation from the current text selection.
   * Color: #7A6A8D (purple). Opens the reference document panel in Viewer.js.
   *
   * In redefine mode, calls setUpdatedXFDF instead of addAnnotation so the
   * existing reference can be updated with the new highlight position.
   */
  pickReferencePaperView = async () => {
    if (this.props.hideClaimsPanel) this.props.hideClaimsPanel();
    if (this.props.hideLinkClaimPanel) this.props.hideLinkClaimPanel();

    var result = await this.createAnnotationFromSelection(
      "reference",
      "#7A6A8D",
      0.5
    );
    if (!result) return;

    this.setState({
      requestedAnnotation: "reference",
      currentAnnotation: result.annotationId,
      currentAnnotationXfdf: result.xfdf,
      currentPageNumber: parseInt(result.pageIndex) + 1,
    });

    // Open reference panels and close competing ones
    if (!this.props.inRedefineMode) {
      if (this.props.closeConversation) this.props.closeConversation();
      if (this.props.closeAttachmentsPanel) this.props.closeAttachmentsPanel();
      if (this.props.openReferenceDocumentPanel)
        this.props.openReferenceDocumentPanel();
      if (this.props.linkingReferences) this.props.linkingReferences();
      if (this.props.closeDocumentReferencePanel)
        this.props.closeDocumentReferencePanel();
      if (this.props.setCurrentAnnotationText)
        this.props.setCurrentAnnotationText(
          this.state.currentAnnotationText || ""
        );
    }

    // Handle redefine mode vs normal mode
    if (this.props.inRedefineMode) {
      if (this.props.setUpdatedXFDF) {
        this.props.setUpdatedXFDF(result.xfdf, result.annotationId);
      }
    } else {
      if (this.props.addAnnotation) {
        this.props.addAnnotation({
          id: result.annotationId,
          xfdf: result.xfdf,
          type: "reference",
        });
      }
    }
    this.storeAnnotationInLocalStorage(
      result.annotationId,
      result.xfdf,
      result.pageIndex
    );

    if (this.props.onTextSelection) {
      this.props.onTextSelection(
        "reference",
        result.pageIndex,
        result.annotationId,
        result.xfdf
      );
    }
  };

  /**
   * Creates a strikeout annotation from the current text selection.
   * Color: #E44234 (red), opacity 1. Uses STRIKEOUT type (12) instead
   * of HIGHLIGHT (9). Triggers Viewer.js addStrikeout flow which creates
   * a conversation with conversation_type "strikethrough".
   */
  pickStrikeoutPaperView = async () => {
    if (this.props.hideClaimsPanel) this.props.hideClaimsPanel();
    if (this.props.hideLinkClaimPanel) this.props.hideLinkClaimPanel();

    var result = await this.createAnnotationFromSelection(
      "strikeout",
      "#E44234",
      1
    );
    if (!result) return;

    this.setState({
      requestedAnnotation: "strikeout",
      currentAnnotation: result.annotationId,
      currentAnnotationXfdf: result.xfdf,
      currentPageNumber: parseInt(result.pageIndex) + 1,
    });

    if (this.props.addAnnotation) {
      this.props.addAnnotation({
        id: result.annotationId,
        xfdf: result.xfdf,
      });
    }
    this.storeAnnotationInLocalStorage(
      result.annotationId,
      result.xfdf,
      result.pageIndex
    );

    if (this.props.onTextSelection) {
      this.props.onTextSelection(
        "strikeout",
        result.pageIndex,
        result.annotationId,
        result.xfdf
      );
    }
  };

  /**
   * Creates a claim highlight annotation from the current text selection.
   * Color: #B6EAD4 (green). Opens the LinkClaimPanel via showLinkClaimPanel
   * and submits claim data to the server via onClaimAnnotation.
   */
  pickClaimPaperView = async () => {
    if (this.props.showLinkClaimPanel) this.props.showLinkClaimPanel();

    var result = await this.createAnnotationFromSelection(
      "claim",
      "#B6EAD4",
      0.5
    );
    if (!result) return;

    // Track the claim annotation ID for visibility toggles
    var claimAnnotationIds = this.state.claimAnnotationIds;
    if (claimAnnotationIds.indexOf(result.annotationId) === -1) {
      claimAnnotationIds.push(result.annotationId);
    }

    this.setState({
      requestedAnnotation: "claim",
      currentAnnotation: result.annotationId,
      currentAnnotationXfdf: result.xfdf,
      currentPageNumber: parseInt(result.pageIndex) + 1,
      claimXFDF: result.xfdf,
      claimAnnotationIds: claimAnnotationIds,
    });

    // Close competing panels
    if (this.props.closeDocumentReferencePanel)
      this.props.closeDocumentReferencePanel();

    // Submit claim annotation data to parent
    if (this.props.onClaimAnnotation) {
      this.props.onClaimAnnotation({
        document_id: this.props.docId,
        library_id: this.props.libraryId,
        page_number: parseInt(result.pageIndex) + 1,
        xfdf_highlight_id: result.annotationId,
        xfdf_highlight_string: result.xfdf,
        xfdf_coords: JSON.stringify(result.rect),
        caption: this.state.currentAnnotationText || "",
        label: null,
      });
    }

    if (this.props.addAnnotation) {
      this.props.addAnnotation({
        id: result.annotationId,
        xfdf: result.xfdf,
        type: "claim",
      });
    }
    this.storeAnnotationInLocalStorage(
      result.annotationId,
      result.xfdf,
      result.pageIndex
    );

    if (this.props.onTextSelection) {
      this.props.onTextSelection(
        "claim",
        result.pageIndex,
        result.annotationId,
        result.xfdf
      );
    }
  };

  /**
   * Generates the document URL for Paperview PDF to load.
   * Handles both regular documents and version-specific URLs.
   */
  /**
   * The document ID whose file the viewer displays — the selected version
   * when one is set, otherwise the main document.
   */
  getActiveDocumentId = () => {
    const { docId, versionId } = this.props;

    let documentId = docId;
    if (versionId && versionId > 0 && versionId !== docId) {
      documentId = versionId;
    }

    return documentId;
  };

  /**
   * Starts (or restarts) waiting for the backend's PDF rendition of the
   * active document. The viewer is not initialized until the rendition is
   * ready (see render()); after the poll timeout an error notice is shown
   * instead.
   */
  startPdfReadyWait = () => {
    const documentId = this.getActiveDocumentId();

    // Only trust the preloaded document JSON when it describes the document
    // actually being displayed (not another version of it).
    const preloadedDocument =
      this.props.doc && this.props.doc.id === documentId
        ? this.props.doc
        : null;

    if (this.cancelPdfReadyWait) {
      this.cancelPdfReadyWait();
    }

    this.cancelPdfReadyWait = waitForPdfReady({
      documentId: documentId,
      document: preloadedDocument,
      fileUrl: this.getDocumentUrl(),
      onReady: () => {
        this.setState({ pdfReady: true });
      },
      onTimeout: () => {
        this.setState({ pdfTimedOut: true });
      },
    });
  };

  getDocumentUrl = () => {
    const token = security.getToken();
    const documentId = this.getActiveDocumentId();

    // Build the document URL with authentication
    const baseUrl = loadDocumentUrl();
    // PaperView PDF only supports .pdf format
    const extension = ".pdf";

    // The backend expects the document file endpoint
    // Token is passed as a query parameter for authentication
    const documentUrl =
      baseUrl +
      "/documents/" +
      documentId +
      "/file" +
      extension +
      "?token=" +
      token;

    return documentUrl;
  };

  /**
   * Called when Paperview PDF viewer is ready.
   * Stores references to the registry and all required plugins.
   *
   * Initialization order matters:
   * 1. Store plugin references
   * 2. Register selection commands (comment, reference, strikeout, claim)
   * 3. Configure selection menu based on current mode
   * 4. Subscribe to selection events (text highlight tracking)
   * 5. Load annotations from server (XFDF import)
   * 6. THEN subscribe to annotation/shape events (after import finishes,
   *    so imported annotations don't trigger panel-opening handlers)
   */
  handleViewerReady = (registry) => {
    this.registry = registry;

    // Get plugin references for later use
    // Using provides() to get the capability interface
    var annotationPluginProvider = registry.getPlugin("annotation");
    if (annotationPluginProvider) {
      this.annotationPlugin = annotationPluginProvider.provides();
    }

    var selectionPluginProvider = registry.getPlugin("selection");
    if (selectionPluginProvider) {
      this.selectionPlugin = selectionPluginProvider.provides();
    }

    var xfdfPluginProvider = registry.getPlugin("xfdf");
    if (xfdfPluginProvider) {
      this.xfdfPlugin = xfdfPluginProvider.provides();
    }

    var documentManagerProvider = registry.getPlugin("document-manager");
    if (documentManagerProvider) {
      this.documentManagerPlugin = documentManagerProvider.provides();
    }

    var compareProvider = registry.getPlugin("compare");
    if (compareProvider) {
      this.comparePlugin = compareProvider.provides();
      // Findings handed over before the viewer was ready
      this.applyReviewFindings();
    }

    var zoomProvider = registry.getPlugin("zoom");
    if (zoomProvider) {
      this.zoomPlugin = zoomProvider.provides();

      // Remember the user's zoom level for the next document they open.
      // event.newZoom is the resulting scale factor; event.level is only the
      // base the request started from (zoomIn passes the previous level).
      this.zoomUnsubscribe = this.zoomPlugin.onZoomChange(function (event) {
        localStorage.setItem(ZOOM_LEVEL_STORAGE_KEY, String(event.newZoom));
      });
    }

    var commandsProvider = registry.getPlugin("commands");
    if (commandsProvider) {
      this.commandsPlugin = commandsProvider.provides();
    }

    var uiProvider = registry.getPlugin("ui");
    if (uiProvider) {
      this.uiPlugin = uiProvider.provides();
    }

    var scrollProvider = registry.getPlugin("scroll");
    if (scrollProvider) {
      this.scrollPlugin = scrollProvider.provides();
    }

    // Register custom commands for text selection actions
    this.registerSelectionCommands();

    // Comment / Reference popup of a freshly drawn shape, hidden until a
    // shape is waiting for the choice
    this.registerShapeIntentCommands();
    if (this.uiPlugin) {
      this.uiPlugin.disableCategory(SHAPE_INTENT_MENU_CATEGORY);
    }

    // Configure selection menu based on current mode
    this.updateSelectionMenu(this.props);

    // Pin drop toolbar button, dropdown and commands
    this.registerPinCommands();
    this.updatePinToolbar(this.props);

    // Subscribe to selection events for state tracking
    this.subscribeToSelectionEvents();

    this.setState({ viewerReady: true, webViewerLoaded: true });

    // Load annotations first, then subscribe to events.
    // This prevents imported annotations from triggering selection/creation handlers.
    var _that = this;
    var docId =
      this.props.versionId > 0 ? this.props.versionId : this.props.docId;

    // Wait for the document's page layout before importing annotations.
    // The registry becomes ready before the engine has opened the document;
    // importing XFDF at that point is silently lost, which left annotations
    // missing until a visibility toggle re-imported them. onLayoutReady is a
    // behavior emitter: it replays immediately when layout is already done.
    var layoutReadyPromise = new Promise(function (resolve) {
      if (!_that.scrollPlugin || !_that.scrollPlugin.onLayoutReady) {
        resolve();
        return;
      }

      var layoutAlreadyReady = false;
      var unsubscribeLayoutReady = _that.scrollPlugin.onLayoutReady(
        function () {
          layoutAlreadyReady = true;
          if (unsubscribeLayoutReady) {
            unsubscribeLayoutReady();
            unsubscribeLayoutReady = null;
          }
          resolve();
        }
      );

      // The replay case runs the callback before the unsubscribe handle
      // exists — clean up here instead.
      if (layoutAlreadyReady && unsubscribeLayoutReady) {
        unsubscribeLayoutReady();
        unsubscribeLayoutReady = null;
      }
    });

    var loadPromise = docId
      ? layoutReadyPromise.then(function () {
          return _that.loadAnnotationsPaperView(docId);
        })
      : Promise.resolve();

    loadPromise.then(function () {
      _that.subscribeToAnnotationEvents();
      _that.subscribeToAnnotationLifecycleEvents();
    });

    // Notify parent after page layout is ready so that initial page
    // navigation (e.g. ?page=3) can scroll reliably.
    if (this.props.setAnnotationsLoaded) {
      if (this.scrollPlugin && this.scrollPlugin.onLayoutReady) {
        var setLoaded = this.props.setAnnotationsLoaded;
        var unsubscribe = this.scrollPlugin.onLayoutReady(function () {
          setLoaded(true);
          if (unsubscribe) {
            unsubscribe();
          }
        });
      } else {
        // Fallback if scroll plugin unavailable
        this.props.setAnnotationsLoaded(true);
      }
    }
  };

  /**
   * Called when Paperview PDF encounters an error.
   */
  handleViewerError = (error) => {
    notificationService.addNotification(
      "Error loading document viewer",
      "Please refresh the page and try again.",
      "danger"
    );
  };

  // ===========================================
  // Paperview PDF XFDF Import Methods
  // ===========================================

  /**
   * Returns true if an annotation with this ID is already rendered in the viewer.
   *
   * Why this exists: Legacy XFDF strings stored in the database are "bloated" —
   * each conversation's xfdf_highlight_string contains every annotation on the
   * document, not just its own. Without this check, loading a document with
   * 5 conversations would import the same annotations 5 times.
   */
  annotationAlreadyImported = (annotationId) => {
    if (!this.annotationPlugin) return false;

    var state = this.annotationPlugin.getState();
    if (!state || !state.byUid) return false;

    return state.byUid[annotationId] ? true : false;
  };

  /**
   * Imports a single XFDF string into the viewer.
   * Wrapper around xfdfPlugin.importXfdf with error handling.
   * The xfdf plugin handles both standard XFDF (<annots>) and
   * PDFTron command format (<add>/<modify>/<delete>) transparently.
   */
  importXfdfString = async (xfdfString) => {
    if (!this.xfdfPlugin) {
      return [];
    }

    if (!xfdfString) {
      return [];
    }

    try {
      var result = await this.xfdfPlugin.importXfdf(xfdfString);
      return result || [];
    } catch (error) {
      return [];
    }
  };

  /**
   * Imports the annotation of a conversation or a reference.
   *
   * Pin drops (stamps) cannot go through the xfdf plugin: it does not carry
   * the stamp's image, so the pin would be imported without an appearance
   * and stay invisible. Pins are imported with their image by importPinXfdf;
   * everything else goes through importXfdfString.
   *
   * @param {string} xfdfString - the record's xfdf_highlight_string
   * @param {string} annotationId - the record's xfdf_highlight_id
   * @param {string} pinType - PIN_TYPES value; the pin image to use when the
   *   annotation turns out to be a pin
   */
  importAnnotationXfdf = async (xfdfString, annotationId, pinType) => {
    var isPinImported = false;

    try {
      isPinImported = await importPinXfdf({
        annotationPlugin: this.annotationPlugin,
        documentManagerPlugin: this.documentManagerPlugin,
        xfdfString: xfdfString,
        annotationId: annotationId,
        type: pinType,
      });
    } catch (error) {
      console.warn("[pin-drop] Pin import failed", annotationId, error);
    }

    if (isPinImported) return;

    await this.importXfdfString(xfdfString);
  };

  /**
   * Loads all annotations for the document (references, conversations, claims, signatures).
   * Called after the viewer is ready.
   */
  loadAnnotationsPaperView = async (docId) => {
    try {
      // Fetch from server then import XFDF into the viewer.
      // Each import is awaited so annotations are fully rendered before
      // subscribeToAnnotationEvents / subscribeToShapeAnnotationEvents
      // attach their listeners (prevents import events from opening panels).
      var refs = await this.props.fetchDocumentReferences(docId);
      await this.importDocumentReferencesPaperView(refs);

      var convos = await this.props.fetchDocumentConversations(docId);
      await this.importConversationsPaperView(convos);

      var docClaims = await this.props.fetchDocumentClaims(docId);
      await this.importDocumentClaimsPaperView(docClaims);

      await this.importClaimsPaperView(this.props.claims);
      await this.importSignaturesPaperView(this.props.signatures);

      // After all Papercurve annotations are imported, hide native PDF
      // annotations if the toggle is set (default: hidden)
      if (this.props.hideNativeAnnotations) {
        this.hideNativeAnnotationsPaperView();
      }

      if (this.props.confirmReferencesLoaded) {
        this.props.confirmReferencesLoaded();
      }
    } catch (error) {
      // Annotation loading failed — viewer still usable, just without annotations
    }
  };

  /**
   * Imports document reference annotations from the server into the viewer.
   * Tracks each reference ID in state.referenceAnnotationIds so
   * subscribeToAnnotationEvents can identify reference clicks vs comment clicks.
   */
  importDocumentReferencesPaperView = async (documentReferences) => {
    if (!documentReferences || !documentReferences.length) return;

    for (var i = 0; i < documentReferences.length; i++) {
      var reference = documentReferences[i];
      var hasXfdf =
        reference.xfdf_highlight_id && reference.xfdf_highlight_string;
      var isNew =
        hasXfdf && !this.annotationAlreadyImported(reference.xfdf_highlight_id);

      if (isNew) {
        await this.importAnnotationXfdf(
          reference.xfdf_highlight_string,
          reference.xfdf_highlight_id,
          PIN_TYPES.REFERENCE
        );
      }

      if (hasXfdf) {
        var referenceAnnotationIds = this.state.referenceAnnotationIds;
        referenceAnnotationIds.push(reference.xfdf_highlight_id);
        this.setState({ referenceAnnotationIds: referenceAnnotationIds });
      }
    }
  };

  /**
   * Imports conversation (comment) annotations.
   * Skips import if the annotation already exists in the viewer
   * (older XFDF strings contain all annotations, causing duplicates).
   * Tracks each conversation ID in state.conversationAnnotationIds.
   */
  importConversationsPaperView = async (conversations) => {
    if (!conversations || !conversations.length) return;

    for (var i = 0; i < conversations.length; i++) {
      var conversation = conversations[i];

      var hasXfdf =
        conversation.xfdf_highlight_id && conversation.xfdf_highlight_string;

      var isNew =
        hasXfdf &&
        !this.annotationAlreadyImported(conversation.xfdf_highlight_id);

      if (isNew) {
        await this.importAnnotationXfdf(
          conversation.xfdf_highlight_string,
          conversation.xfdf_highlight_id,
          PIN_TYPES.COMMENT
        );
      }

      if (hasXfdf) {
        var conversationAnnotationIds = this.state.conversationAnnotationIds;

        if (
          conversationAnnotationIds.indexOf(conversation.xfdf_highlight_id) ===
          -1
        ) {
          conversationAnnotationIds.push(conversation.xfdf_highlight_id);
          this.setState({
            conversationAnnotationIds: conversationAnnotationIds,
          });
        }
      }
    }
  };

  /**
   * Imports document claims annotations.
   * Tracks each claim ID in state.claimAnnotationIds.
   */
  importDocumentClaimsPaperView = async (documentClaims) => {
    if (!documentClaims || !documentClaims.length) return;

    for (var i = 0; i < documentClaims.length; i++) {
      var claim = documentClaims[i];
      var hasXfdf = claim.xfdf_highlight_id && claim.xfdf_highlight_string;
      var isNew =
        hasXfdf && !this.annotationAlreadyImported(claim.xfdf_highlight_id);

      if (isNew) {
        await this.importXfdfString(claim.xfdf_highlight_string);
      }

      if (hasXfdf) {
        var claimAnnotationIds = this.state.claimAnnotationIds;

        if (claimAnnotationIds.indexOf(claim.xfdf_highlight_id) === -1) {
          claimAnnotationIds.push(claim.xfdf_highlight_id);
          this.setState({ claimAnnotationIds: claimAnnotationIds });
        }
      }
    }
  };

  /**
   * Imports claims annotations from props.
   * Tracks each claim ID in state.claimAnnotationIds.
   */
  importClaimsPaperView = async (claims) => {
    if (!claims || !claims.length) return;

    for (var i = 0; i < claims.length; i++) {
      var claim = claims[i];
      var hasXfdf = claim.xfdf_highlight_id && claim.xfdf_highlight_string;
      var isNew =
        hasXfdf && !this.annotationAlreadyImported(claim.xfdf_highlight_id);

      if (isNew) {
        await this.importXfdfString(claim.xfdf_highlight_string);
      }

      if (hasXfdf) {
        var claimAnnotationIds = this.state.claimAnnotationIds;
        if (claimAnnotationIds.indexOf(claim.xfdf_highlight_id) === -1) {
          claimAnnotationIds.push(claim.xfdf_highlight_id);
          this.setState({ claimAnnotationIds: claimAnnotationIds });
        }
      }
    }
  };

  /**
   * Imports signature annotations.
   */
  importSignaturesPaperView = async (signatures) => {
    if (!signatures || !signatures.length) return;

    for (var i = 0; i < signatures.length; i++) {
      var signature = signatures[i];
      var isNew =
        signature.annotation_xfdf &&
        signature.annotation_id &&
        !this.annotationAlreadyImported(signature.annotation_id);

      if (isNew) {
        await this.importXfdfString(signature.annotation_xfdf);
      }
    }

    // Update saving state if signatures exist
    if (signatures.length > 0) {
      this.props.updateSavingContentState &&
        this.props.updateSavingContentState(SAVING_CONTENT_STATES.SAVED);
    }
  };

  /**
   * Reloads references annotations (for visibility toggle).
   */
  reloadReferencesPaperView = async (references) => {
    if (!references || !references.length) return;

    for (var i = 0; i < references.length; i++) {
      var reference = references[i];

      if (reference.xfdf_highlight_id && reference.xfdf_highlight_string) {
        await this.importAnnotationXfdf(
          reference.xfdf_highlight_string,
          reference.xfdf_highlight_id,
          PIN_TYPES.REFERENCE
        );

        var referenceAnnotationIds = this.state.referenceAnnotationIds;
        if (
          referenceAnnotationIds.indexOf(reference.xfdf_highlight_id) === -1
        ) {
          referenceAnnotationIds.push(reference.xfdf_highlight_id);
          this.setState({ referenceAnnotationIds: referenceAnnotationIds });
        }
      }
    }
  };

  /**
   * Reloads conversation annotations (for visibility toggle).
   */
  reloadConversationsPaperView = async (conversations) => {
    if (!conversations || !conversations.length) return;

    for (var i = 0; i < conversations.length; i++) {
      var conversation = conversations[i];

      if (
        conversation.xfdf_highlight_id &&
        conversation.xfdf_highlight_string
      ) {
        await this.importAnnotationXfdf(
          conversation.xfdf_highlight_string,
          conversation.xfdf_highlight_id,
          PIN_TYPES.COMMENT
        );
      }
    }
    this.props.resetConversationStatusToggle &&
      this.props.resetConversationStatusToggle();
  };

  /**
   * Reloads claims annotations (for visibility toggle).
   */
  reloadClaimsPaperView = async (claims) => {
    if (!claims || !claims.length) return;

    for (var i = 0; i < claims.length; i++) {
      var claim = claims[i];
      if (claim.xfdf_highlight_id && claim.xfdf_highlight_string) {
        await this.importXfdfString(claim.xfdf_highlight_string);
      }
    }
  };

  render() {
    if (!this.state.pdfReady) {
      return (
        <div className={styles.webViewerContainer} id="webViewerContainer">
          <div className={styles.processingContainer}>
            {this.state.pdfTimedOut ? (
              <div>
                Your document could not be processed. Please refresh the page to
                try again.
              </div>
            ) : (
              <Spinner text="Processing document..." />
            )}
          </div>
        </div>
      );
    }

    const documentUrl = this.getDocumentUrl();

    return (
      <div className={styles.webViewerContainer} id="webViewerContainer">
        <PaperviewPDF
          config={{
            src: documentUrl,
            wasmUrl: "/pdfium.wasm",
            worker: true,
            theme: {
              preference: "light",
            },
            zoom: {
              // Open at the user's remembered zoom level
              defaultZoomLevel: loadStoredZoomLevel() || "fit-page",
            },
            // Hide "Annotate" and "Redact" mode tabs from the toolbar.
            // Papercurve uses custom annotation tools via the text selection popup.
            disabledCategories: [
              "mode-annotate",
              "mode-redact",
              "panel-comment",
            ],
            // Pin drop tools (comment pin and reference pin)
            annotations: {
              tools: PIN_DROP_TOOLS,
            },
          }}
          onReady={this.handleViewerReady}
          onError={this.handleViewerError}
          style={{ width: "100%", height: "100%" }}
        />
      </div>
    );
  }
}

PaperViewer.propTypes = {
  doc: PropTypes.object,
  docId: PropTypes.number,
  docExtension: PropTypes.string,
  versionId: PropTypes.number,
  libraryId: PropTypes.number,
  isApproved: PropTypes.bool,
  lockDownCRV: PropTypes.bool,
  readOnly: PropTypes.bool,
  inRedefineMode: PropTypes.bool,
  conversationsInitiated: PropTypes.array,
  onTextSelection: PropTypes.func,
  onAnnotationSelect: PropTypes.func,
  onAnnotationDeselect: PropTypes.func,
  addAnnotation: PropTypes.func,
  updateAnnotationXfdf: PropTypes.func,
  onClaimAnnotation: PropTypes.func,
  hideClaimsPanel: PropTypes.func,
  hideLinkClaimPanel: PropTypes.func,
  showLinkClaimPanel: PropTypes.func,
  closeConversation: PropTypes.func,
  onCloseConversation: PropTypes.func,
  closeDocumentReferencePanel: PropTypes.func,
  closeReferenceDocumentPanel: PropTypes.func,
  closeAttachmentsPanel: PropTypes.func,
  openReferenceDocumentPanel: PropTypes.func,
  linkingReferences: PropTypes.func,
  setCurrentAnnotationText: PropTypes.func,
  setUpdatedXFDF: PropTypes.func,
  setConversationsInitiated: PropTypes.func,
  setPreviousConvoId: PropTypes.func,
};

const mapStateToProps = (state) => {
  return {
    documentClaims: state.documentClaims,
    documentReferences: state.documentReferences,
    documentConversations: state.documentConversations,
  };
};

const actionCreators = {
  fetchDocumentClaims,
  fetchDocumentReferences,
  fetchDocumentConversations,
};

export default connect(mapStateToProps, actionCreators)(PaperViewer);
